import { useEffect, useMemo, useRef, useState } from "react";
import { htmlPreviewLinkScriptUrl, htmlPreviewUrl, startHtmlPreviewSession } from "@/lib/api";
import { PreviewSkeleton } from "@/components/preview/PreviewSkeleton";
import { openEnvironmentFileByPath } from "@/lib/environment-files";
import {
	HTML_PREVIEW_LINK_MESSAGE,
	isExternalLinkHref,
	toHtmlPreviewSrcDoc,
	workspacePathFromPreviewHref,
} from "@/lib/html-preview-document";

/**
 * allow-same-origin を付けないことで iframe 内は不透明オリジンになり、親ページの DOM・Cookie・
 * ストレージへ届かず、認証 Cookie (SameSite=Strict) も API へ送られない。エージェントが生成した
 * HTML のスクリプトはこの隔離内でだけ動く。フォーム送信・ポップアップ・親の遷移も許可しない。
 * バックエンドも同じ制限の CSP sandbox を付けて配信するので、URL を直接開いても隔離される。
 */
const SANDBOX = "allow-scripts";

interface HtmlPreviewProps {
	environmentId: string;
	/** ワークスペース相対のパス。HTML 内の相対パス (img・video 等) はこのパスのディレクトリを基準に解決される。 */
	path: string;
	version?: string | number;
	filename: string;
	/**
	 * 指定すると URL ではなくこの文字列を srcdoc で表示する。Source タブでの未保存編集内容を
	 * Preview タブに反映するために使う (呼び出し側が dirty のときだけ渡す想定)。
	 */
	source?: string;
}

/**
 * HTML を sandbox 付き iframe に描く。不透明オリジンからはログイン Cookie が送られないため、
 * プレビュー専用の Cookie を受け取ってからパス付きの URL で配信し、相対パスの画像・動画も取得できるようにする。
 * リンクは iframe 内で遷移させず、同じ環境のファイルはアプリの新しいファイルタブ、外部はブラウザの新しいタブで開く。
 */
export function HtmlPreview({ environmentId, path, version, filename, source }: HtmlPreviewProps) {
	// Cookie を発行し終えた環境と版。版が変わるたびに発行し直すことで、長時間開いたままでも
	// 期限切れの Cookie で再読み込みせず、前回の失敗からも回復する。発行し終えるまでは前の版を表示し続ける。
	const [issued, setIssued] = useState<{ environmentId: string; version?: string | number } | null>(
		null,
	);
	const [error, setError] = useState<string | null>(null);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const previewRootHref = new URL(htmlPreviewUrl(environmentId, ""), window.location.href).href;

	useEffect(() => {
		let cancelled = false;
		setError(null);
		startHtmlPreviewSession(environmentId)
			.then(() => {
				if (!cancelled) setIssued({ environmentId, version });
			})
			.catch((err: unknown) => {
				console.error("HtmlPreview session start failed", err);
				if (!cancelled) setError("プレビューを読み込めませんでした。");
			});
		return () => {
			cancelled = true;
		};
	}, [environmentId, version]);

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			// iframe 内の HTML は信頼できず、クリックを経ずに直接 postMessage できる。ユーザー操作の判定は
			// アプリのどこを操作しても立つため、フォーカスが iframe にある (直前の操作がプレビュー内) ときに限る。
			const iframe = iframeRef.current;
			if (
				!iframe ||
				event.source !== iframe.contentWindow ||
				document.activeElement !== iframe ||
				!navigator.userActivation.isActive
			)
				return;
			const data: unknown = event.data;
			if (
				typeof data !== "object" ||
				data === null ||
				(data as { type?: unknown }).type !== HTML_PREVIEW_LINK_MESSAGE
			)
				return;
			const href = (data as { href?: unknown }).href;
			if (typeof href !== "string" || !URL.canParse(href)) return;
			const linkedPath = workspacePathFromPreviewHref(href, previewRootHref);
			if (linkedPath !== null) {
				void openEnvironmentFileByPath(linkedPath, { asPreview: false });
			} else if (isExternalLinkHref(href, previewRootHref)) {
				window.open(href, "_blank", "noopener,noreferrer");
			}
		};
		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [previewRootHref]);

	const url =
		issued?.environmentId === environmentId
			? htmlPreviewUrl(environmentId, path, issued.version)
			: null;
	const srcDoc = useMemo(
		() =>
			url === null || source === undefined
				? undefined
				: toHtmlPreviewSrcDoc(
						source,
						new URL(url, window.location.href).href,
						new URL(htmlPreviewLinkScriptUrl(), window.location.href).href,
					),
		[url, source],
	);

	if (error) return <p className="m-0 p-4 text-caption text-danger">{error}</p>;
	if (url === null) return <PreviewSkeleton />;

	// 背景を指定しない HTML は iframe が透過して暗い地に黒文字が乗るため、白紙を敷く。
	return (
		<iframe
			ref={iframeRef}
			{...(srcDoc === undefined ? { src: url } : { srcDoc })}
			sandbox={SANDBOX}
			referrerPolicy="no-referrer"
			title={filename}
			data-testid="html-preview"
			className="h-full w-full border-0 bg-html-preview-bg"
		/>
	);
}
